import { components } from "@certn/api/generated-schemas/client-portal";
import { Modal, PrimaryButton, Stack, Text } from "@certn/cascadia";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Divider,
  GhostIconButton,
  Search,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCanViewClientPortalPricing } from "../../../../permissions/Permissions";
import { getCheckDisplayNameFromCheck } from "../utils";
import { formatNullableCurrency } from "../../../../../utils/formatNullableCurrency";

interface Props {
  show?: boolean;
  onClose: () => void;
  checks: components["schemas"]["CheckListChild"][];
}

export function CriminalRecordsFullListModal(props: Props) {
  const { show = false, onClose, checks } = props;
  const { t } = useTranslation();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const [searchQuery, setSearchQuery] = useState("");

  const sortedChecks = useMemo(() => {
    return [...checks].sort((a, b) => {
      const aDisplayName = getCheckDisplayNameFromCheck(a, "FULL_LIST");
      const bDisplayName = getCheckDisplayNameFromCheck(b, "FULL_LIST");

      return (aDisplayName || "").localeCompare(bDisplayName || "");
    });
  }, [checks]);

  const filteredSortedChecks = useMemo(() => {
    if (!searchQuery.trim()) {
      return sortedChecks;
    }

    const query = searchQuery.toLowerCase();
    return sortedChecks.filter((check) => {
      const coverage = check.coverage?.toLowerCase() || "";
      const name = check.name?.toLowerCase() || "";
      return coverage.includes(query) || name.includes(query);
    });
  }, [sortedChecks, searchQuery]);

  if (!show) {
    return null;
  }

  const hasExcludedChecks = filteredSortedChecks.some(
    (check) => !!check.name.includes("*")
  );

  return (
    <Modal isOpen={show} testID="criminal-records-full-list-modal">
      <Dialog width="560px">
        <DialogHeader onClose={onClose}>
          <GhostIconButton
            onPress={onClose}
            size="medium"
            icon="arrow_back"
            aria-label={t("Close")}
          />
          <Text color="text-base" style="global/modal-header">
            {t("Criminal record checks by country")}
          </Text>
        </DialogHeader>
        <DialogContent>
          <div className={styles.descriptionContainer}>
            <Text style="form/body-primary">
              {t(
                "Depending on the country, results may include information on arrests and convictions, warrants or outstanding charges, court records, and national registries such as sex offender registries."
              )}
            </Text>
            {hasExcludedChecks && (
              <div>
                <br />
                <Text style="form/body-primary">
                  {t(
                    "Please note: countries marked with an asterisk (*) are not eligible for address-based ordering."
                  )}
                </Text>
              </div>
            )}
          </div>

          <Search
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t("Search")}
            aria-label={t("Search")}
            testID="criminal-records-search-input"
            debounce={0}
            maxWidth="560px"
          />

          {filteredSortedChecks.map((check) => {
            const displayName = getCheckDisplayNameFromCheck(
              check,
              "FULL_LIST"
            );
            const formattedPrice = formatNullableCurrency(check.price, {
              currency: check.currency,
            });

            return (
              <div key={check.type || check.name}>
                <div className={styles.listItem}>
                  <Stack orientation="horizontal">
                    <Text style="global/heading-medium" marginTop="space-4">
                      {displayName}
                    </Text>
                  </Stack>
                  <Stack orientation="horizontal">
                    <Text
                      style="global/heading-medium"
                      color="text-accent-tofino"
                    >
                      {canViewClientPortalPricing ? formattedPrice : ""}
                    </Text>
                  </Stack>
                </div>
                <Divider />
              </div>
            );
          })}

          {filteredSortedChecks.length === 0 && (
            <Text style="form/body-primary" color="text-weak">
              {t("No results found")}
            </Text>
          )}
        </DialogContent>
        <DialogFooter>
          <PrimaryButton
            size="medium"
            onPress={onClose}
            aria-label={t("Close button")}
            testID="criminal-records-full-list-modal-close-button"
          >
            {t("Close")}
          </PrimaryButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

const styles = {
  descriptionContainer: css({
    marginBottom: "var(--space-4)",
  }),
  listItem: css({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    padding: `${"var(--space-2)"} ${"var(--space-0)"} ${"var(--space-2)"} ${"var(--space-0)"}`,
  }),
};
